import * as React from "react"

import { cn } from "@/lib/utils"

type Ctx = { 
  open: boolean
  setOpen: (open: boolean) => void
  focusedIndex: number
  setFocusedIndex: (index: number) => void
  itemsCount: number
  setItemsCount: (count: number) => void
} | null

const DropdownCtx = React.createContext<Ctx>(null)

const DropdownMenu = ({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const [open, setOpen] = React.useState(false)
  const [focusedIndex, setFocusedIndex] = React.useState(-1)
  const [itemsCount, setItemsCount] = React.useState(0)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (!open) return
      
      if (e.key === "Escape") {
        setOpen(false)
        setFocusedIndex(-1)
        return
      }
      
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setFocusedIndex((prev) => (prev + 1) % itemsCount)
        return
      }
      
      if (e.key === "ArrowUp") {
        e.preventDefault()
        setFocusedIndex((prev) => (prev - 1 + itemsCount) % itemsCount)
        return
      }
    }
    
    document.addEventListener("mousedown", onDocClick)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDocClick)
      document.removeEventListener("keydown", onKey)
    }
  }, [open, itemsCount])

  // Reset focus when opening/closing
  React.useEffect(() => {
    if (!open) {
      setFocusedIndex(-1)
    }
  }, [open])

  return (
    <DropdownCtx.Provider value={{ open, setOpen, focusedIndex, setFocusedIndex, itemsCount, setItemsCount }}>
      <div ref={ref} className={cn("relative inline-block", className)} {...props}>
        {children}
      </div>
    </DropdownCtx.Provider>
  )
}

const DropdownMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, onClick, ...props }, ref) => {
  const ctx = React.useContext(DropdownCtx)
  return (
    <button
      ref={ref}
      className={cn("inline-flex items-center justify-center", className)}
      onClick={(e) => {
        onClick?.(e)
        ctx?.setOpen(!ctx.open)
      }}
      {...props}
    />
  )
})
DropdownMenuTrigger.displayName = "DropdownMenuTrigger"

const DropdownMenuContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const ctx = React.useContext(DropdownCtx)
  
  // Deep clone children and inject unique itemIndex for every DropdownMenuItem (even nested)
  let itemIndex = 0
  const assignIndices = (nodes: React.ReactNode): React.ReactNode => {
    return React.Children.map(nodes as React.ReactNode, (child) => {
      if (!React.isValidElement(child)) return child
      if (child.type === DropdownMenuItem) {
        const currentIndex = itemIndex++
        return React.cloneElement(child as React.ReactElement<any>, { itemIndex: currentIndex })
      }
      if ((child as any).props && (child as any).props.children) {
        const processed = assignIndices((child as any).props.children)
        return React.cloneElement(child as React.ReactElement<any>, { children: processed })
      }
      return child
    })
  }
  const childrenWithIndices = assignIndices(children)
  
  // Update items count
  React.useEffect(() => {
    ctx?.setItemsCount(itemIndex)
  }, [ctx, itemIndex, children])
  
  return (
    <div
      ref={ref}
      className={cn(
        "absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg z-50 border border-gray-200",
        !ctx?.open && "hidden",
        className
      )}
      role="menu"
      aria-orientation="vertical"
      {...props}
    >
      {childrenWithIndices}
    </div>
  )
})
DropdownMenuContent.displayName = "DropdownMenuContent"

const DropdownMenuItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { itemIndex?: number }
>(({ className, onClick, itemIndex = 0, ...props }, _ref) => {
  const ctx = React.useContext(DropdownCtx)
  const itemRef = React.useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = React.useState(false)
  const isFocused = ctx?.focusedIndex === itemIndex
  
  // Scroll into view when focused
  React.useEffect(() => {
    if (isFocused && itemRef.current) {
      itemRef.current.scrollIntoView({ block: 'nearest' })
    }
  }, [isFocused])
  
  return (
    <div
      ref={itemRef}
      className={cn(
        "block px-4 py-2 text-sm text-gray-700 cursor-pointer transition-colors",
        (isHovered || isFocused) && "bg-gray-100 text-gray-900",
        className
      )}
      role="menuitem"
      tabIndex={-1}
      onClick={(e) => {
        onClick?.(e)
        ctx?.setOpen(false)
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>)
          ctx?.setOpen(false)
        }
      }}
      onMouseEnter={() => {
        setIsHovered(true)
        ctx?.setFocusedIndex(itemIndex)
      }}
      onMouseLeave={() => {
        setIsHovered(false)
        ctx?.setFocusedIndex(-1)
      }}
      {...props}
    />
  )
})
DropdownMenuItem.displayName = "DropdownMenuItem"

const DropdownMenuSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("h-px bg-gray-200", className)}
    {...props}
  />
))
DropdownMenuSeparator.displayName = "DropdownMenuSeparator"

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
}

